import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  Loader2,
  Save,
  ArrowLeft,
  Plus,
  Trash2,
  ImageIcon,
  Link as LinkIcon,
  Upload
} from 'lucide-react';

import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';

// Import fetchProductById, createProduct, updateProduct from api (fetchCategories removed)
import { fetchProductById, createProduct, updateProduct } from '@/lib/api';
import { isValidUrl } from '@/lib/utils';
import { uploadFile } from '@/integrations/supabase/storage';
import ImageHandler from '@/components/ImageHandler';
// Import getCategories and fetchSubcategories from categories
import { getCategories, getSubcategoriesWithCategoryName as fetchSubcategories } from '@/lib/categories'; // <-- Added getCategories here

// Updated Zod schema to include subcategory_id
const formSchema = z.object({
  name: z.string().min(3, "Nome muito curto").max(255, "Nome muito longo"),
  description: z.string().min(10, "Descrição muito curta"),
  full_description: z.string().optional(),
  price: z.string().refine(val => !isNaN(parseFloat(String(val).replace(',', '.'))) && parseFloat(String(val).replace(',', '.')) >= 0, {
    message: "Preço deve ser um número válido maior ou igual a zero.",
  }),
  stock: z.string().refine(val => /^[0-9]+$/.test(val) && parseInt(val, 10) >= 0, {
    message: "Estoque deve ser um número inteiro maior ou igual a zero.",
  }),
  category_id: z.string().nullable(),
  subcategory_id: z.string().nullable(), // Add subcategory_id
  image_url: z.string().url("Por favor, insira uma URL válida.").or(z.string().length(0)).nullable(),
  featured: z.boolean().default(false),
  new: z.boolean().default(false),
  status: z.boolean().default(true),
  discount: z.string().optional().refine(val =>
    val === undefined || val === null || val === '' ||
    (!isNaN(parseFloat(String(val).replace(',', '.'))) &&
    parseFloat(String(val).replace(',', '.')) >= 0 &&
    parseFloat(String(val).replace(',', '.')) <= 100), {
    message: "Desconto deve ser um número entre 0 e 100, ou vazio.",
  }),
  additional_image_urls: z.array(z.object({
    id: z.string().optional(),
    url: z.string().url("URL inválida").or(z.string().length(0)).nullable()
  })).optional().default([]),
});

type FormValues = z.infer<typeof formSchema>;

// Interface for the payload sent to the API, including subcategory_id
interface ProductPayload {
  name: string;
  description: string;
  full_description?: string;
  price: string;
  stock: string;
  category_id: string | null;
  subcategory_id: string | null; // Add subcategory_id
  featured: boolean;
  new: boolean;
  status: boolean;
  image_url?: string | null;
  discount?: string | null;
  additional_images?: string[];
  images_to_delete?: string[];
}

const ProductForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('general');
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [additionalImageFiles, setAdditionalImageFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const isEditing = !!id;
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null); // State for selected category

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      full_description: '',
      price: '0',
      category_id: null,
      subcategory_id: null, // Add default subcategory_id
      image_url: '',
      featured: false,
      new: false,
      status: true,
      stock: '0',
      discount: '',
      additional_image_urls: [],
    }
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "additional_image_urls",
  });

  const { data: categories, isLoading: loadingCategories } = useQuery<any[], Error>({
    queryKey: ['categories'],
    // Use getCategories instead of fetchCategories
    queryFn: getCategories // <-- Changed here
  });

  // Fetch subcategories based on selected category
  const { data: subcategories, isLoading: loadingSubcategories } = useQuery<any[], Error>({
    queryKey: ['subcategories', selectedCategoryId],
    queryFn: () => fetchSubcategories(), // This was already using the correct alias
    enabled: !!selectedCategoryId, // Only fetch if a category is selected
  });

  const { data: product, isLoading: loadingProduct } = useQuery<any, Error>({
    queryKey: ['product', id],
    queryFn: () => fetchProductById(id!),
    enabled: !!id,
  });

  // --- Mutations ---
  const createMutation = useMutation<any, Error, ProductPayload>({
    mutationFn: createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Produto criado com sucesso!');
      navigate('/admin/products');
    },
    onError: (error) => {
      console.error("Create product mutation error:", error);
      toast.error(`Erro ao criar produto: ${error.message}`);
    }
  });

  const updateMutation = useMutation<any, Error, { id: string, data: ProductPayload }>( {
    mutationFn: updateProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', id] });
      toast.success('Produto atualizado com sucesso!');
      navigate('/admin/products');
    },
    onError: (error) => {
      console.error("Update product mutation error:", error);
      toast.error(`Erro ao atualizar produto: ${error.message}`);
    }
  });

  const handleRemoveExistingImage = (imageId: string | undefined) => {
    if (imageId && !imagesToDelete.includes(imageId)) {
      setImagesToDelete(prev => [...prev, imageId]);
      console.log("Marked image ID for deletion:", imageId);
    }
  };

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMainImageFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        form.setValue('image_url', event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAdditionalImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      setAdditionalImageFiles(prev => [...prev, ...newFiles]);
      newFiles.forEach(file => {
        const reader = new FileReader();
        reader.onload = (event) => {
          append({ url: event.target?.result as string });
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // --- useEffect to populate form on edit ---
  useEffect(() => {
    if (isEditing && product) {
      const existingAdditionalUrls = product.images?.map((img: any) => ({
        id: img.id,
        url: img.image_url || ''
      })) || [];

      form.reset({
        name: product.name || '',
        description: product.description || '',
        full_description: product.full_description || '',
        price: String(product.price || '0').replace('.', ','),
        stock: String(product.stock || '0'),
        discount: String(product.discount || '').replace('.', ','),
        category_id: product.category_id || null,
        subcategory_id: product.subcategory_id || null, // Set subcategory_id
        image_url: product.image_url || '',
        featured: product.is_featured || false,
        new: product.is_new || false,
        status: product.status === true,
        additional_image_urls: existingAdditionalUrls,
      });
      setSelectedCategoryId(product.category_id || null); // Set selected category on load
      setImagesToDelete([]);
    } else if (!isEditing) {
      form.reset({ // Reset all fields including subcategory
        name: '',
        description: '',
        full_description: '',
        price: '0',
        stock: '0',
        discount: '',
        category_id: null,
        subcategory_id: null, // Reset subcategory
        image_url: '',
        featured: false,
        new: false,
        status: true,
        additional_image_urls: [],
      });
      setSelectedCategoryId(null); // Reset selected category
      setImagesToDelete([]);
    }
  }, [product, isEditing, form]);

   // --- useEffect to clear subcategory if category changes ---
   useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'category_id') {
        const newCategoryId = value.category_id || null;
        // Only update state and reset subcategory if the category actually changed
        if (newCategoryId !== selectedCategoryId) {
            setSelectedCategoryId(newCategoryId);
            form.setValue('subcategory_id', null); // Reset subcategory field
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form.watch, form, selectedCategoryId]); // Add selectedCategoryId to dependency array


  const uploadImages = async () => {
    try {
      setIsUploading(true);
      let mainImageUrl = form.getValues('image_url');
      if (mainImageFile) {
        mainImageUrl = await uploadFile(mainImageFile, 'products');
        console.log("Uploaded main image:", mainImageUrl);
      }

      const additionalUrls: string[] = [];
      fields.forEach((field, index) => {
        const url = form.getValues(`additional_image_urls.${index}.url`); // Corrected syntax
        // Keep existing valid URLs that are not data URIs
        if (url && isValidUrl(url) && !url.startsWith('data:')) {
          additionalUrls.push(url);
        }
      });

      // Upload new files added via the input
      for (const file of additionalImageFiles) {
        try {
          const url = await uploadFile(file, 'products');
          additionalUrls.push(url);
          console.log("Uploaded additional image:", url);
        } catch (error) {
          console.error("Error uploading additional image:", error);
          toast.error("Erro ao fazer upload de uma imagem adicional");
          // Optionally re-throw or handle differently
        }
      }
       // Clear the file inputs after attempting upload
      setMainImageFile(null);
      setAdditionalImageFiles([]);

      return { mainImageUrl, additionalUrls };
    } catch (error) {
      console.error("Error uploading images:", error);
      toast.error("Erro ao fazer upload das imagens");
      throw error; // Re-throw to stop form submission if critical
    } finally {
      setIsUploading(false);
    }
  };

  // --- onSubmit Handler ---
  const onSubmit = async (values: FormValues) => {
    try {
      console.log("onSubmit: Form values received:", values);
      let mainImageUrl = values.image_url;
      let finalAdditionalUrls: string[] = []; // Start with empty array

      // Upload new images if any were selected
      if (mainImageFile || additionalImageFiles.length > 0) {
          const uploadResults = await uploadImages();
          mainImageUrl = uploadResults.mainImageUrl; // Use the uploaded URL for the main image
          finalAdditionalUrls = uploadResults.additionalUrls; // Use the consolidated list from upload function
      } else {
          // If no new files, collect existing valid URLs from the form state (excluding data URIs)
           finalAdditionalUrls = values.additional_image_urls
            ?.map(item => item.url)
            .filter(url => url && isValidUrl(url) && !url.startsWith('data:')) as string[] || [];

      }


      // Include subcategory_id in the payload
      const payload: ProductPayload = {
        name: values.name,
        description: values.description,
        full_description: values.full_description,
        price: values.price.replace(',', '.'), // Ensure price is correctly formatted
        stock: values.stock,
        category_id: values.category_id,
        subcategory_id: values.subcategory_id, // Include subcategory_id
        featured: values.featured,
        new: values.new,
        status: values.status,
        image_url: mainImageUrl, // Use potentially updated main image URL
        discount: values.discount?.replace(',', '.') || null, // Format discount
        additional_images: finalAdditionalUrls, // Use the final list of additional URLs
        images_to_delete: imagesToDelete,
      };

      console.log("onSubmit: Payload being sent to mutation:", payload);

      if (isEditing) {
        if (!id) {
          toast.error("ID do produto não encontrado para atualização.");
          return;
        }
        updateMutation.mutate({ id: id, data: payload });
      } else {
        createMutation.mutate(payload);
      }
    } catch (error) {
      console.error("Error in form submission:", error);
      toast.error("Erro ao processar o formulário");
      // Ensure loading state is reset if submission fails here
      setIsUploading(false);
    }
  };

  // --- Loading State ---
  if ((isEditing && loadingProduct) || loadingCategories) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-lg">Carregando...</span>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="mb-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/admin/products')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar para Produtos
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{isEditing ? 'Editar' : 'Novo'} Produto</CardTitle>
            <CardDescription>
              {isEditing
                ? 'Edite as informações do produto existente.'
                : 'Preencha os detalhes para cadastrar um novo produto.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={tab} onValueChange={setTab} className="space-y-4">
              <TabsList>
                <TabsTrigger value="general">Informações Gerais</TabsTrigger>
                <TabsTrigger value="images">Imagens</TabsTrigger>
                <TabsTrigger value="details">Detalhes</TabsTrigger>
              </TabsList>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <TabsContent value="general">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Basic Info */}
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Nome do Produto</FormLabel>
                              <FormControl>
                                <Input placeholder="Nome do produto" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="description"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Descrição</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Descrição curta do produto"
                                  className="min-h-[100px]"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* --- Category Field --- */}
                        <FormField
                          control={form.control}
                          name="category_id"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Categoria</FormLabel>
                              <Select
                                value={field.value || ''}
                                // Updated onChange logic handled by useEffect watching the field
                                onValueChange={(value) => field.onChange(value || null)}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecione uma categoria" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="">Sem categoria</SelectItem>
                                  {categories?.map((category) => (
                                    <SelectItem key={category.id} value={category.id}>
                                      {category.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* --- Subcategory Field --- */}
                        <FormField
                          control={form.control}
                          name="subcategory_id"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Subcategoria</FormLabel>
                              <Select
                                value={field.value || ''}
                                onValueChange={(value) => field.onChange(value || null)}
                                // Disable if no category selected or subcategories are loading
                                disabled={!selectedCategoryId || loadingSubcategories}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder={
                                      !selectedCategoryId
                                        ? "Selecione uma categoria primeiro"
                                        : loadingSubcategories
                                          ? "Carregando subcategorias..."
                                          : "Selecione uma subcategoria (opcional)"
                                    } />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="">Sem subcategoria</SelectItem>
                                  {/* Render only if not loading and subcategories exist */}
                                  {!loadingSubcategories && subcategories?.map((subcategory) => (
                                    <SelectItem key={subcategory.id} value={subcategory.id}>
                                      {subcategory.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Pricing and Stock */}
                      <div className="space-y-4">
                        <FormField
                          control={form.control}
                          name="price"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Preço (R$)</FormLabel>
                              <FormControl>
                                <Input placeholder="0,00" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="discount"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Desconto (%)</FormLabel>
                              <FormControl>
                                <Input placeholder="Ex: 10" {...field} value={field.value || ''} />
                              </FormControl>
                              <FormDescription>
                                Porcentagem de desconto (opcional)
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="stock"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Estoque</FormLabel>
                              <FormControl>
                                <Input type="number" min="0" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    {/* Status and Flags */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <FormField
                        control={form.control}
                        name="status"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">Ativo</FormLabel>
                              <FormDescription>
                                Produto visível na loja
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="featured"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">Destaque</FormLabel>
                              <FormDescription>
                                Mostrar em destaques
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="new"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                            <div className="space-y-0.5">
                              <FormLabel className="text-base">Novo</FormLabel>
                              <FormDescription>
                                Mostrar como novidade
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="images">
                    <div className="space-y-6">
                      {/* Main Image */}
                      <Card>
                        <CardHeader>
                          <CardTitle>Imagem Principal</CardTitle>
                          <CardDescription>Carregue ou forneça a URL da imagem principal.</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="main-image-file">Carregar Imagem</Label>
                              <Input
                                id="main-image-file"
                                type="file"
                                accept="image/*"
                                onChange={handleMainImageChange}
                                className="mt-1"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Ou insira a URL da imagem abaixo
                              </p>
                            </div>

                            <FormField
                              control={form.control}
                              name="image_url"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>URL da Imagem</FormLabel>
                                  <FormControl>
                                    <Input type="url" placeholder="https://..." {...field} value={field.value || ''} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          {/* Image Preview */}
                          {form.watch('image_url') && (
                            <div className="mt-4">
                              <Label className="mb-2 block">Prévia da Imagem</Label>
                              <div className="border rounded-md overflow-hidden w-full max-w-md">
                                <ImageHandler
                                  src={form.watch('image_url') || ''}
                                  alt="Prévia da imagem principal"
                                  className="w-full h-64 object-cover"
                                />
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Additional Images */}
                      <Card>
                        <CardHeader>
                          <CardTitle>Imagens Adicionais</CardTitle>
                          <CardDescription>Carregue ou forneça URLs de imagens adicionais.</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <div>
                              <Label htmlFor="additional-image-file">Carregar Imagens Adicionais</Label>
                              <Input
                                id="additional-image-file"
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleAdditionalImageChange}
                                className="mt-1"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Ou adicione URLs manualmente abaixo
                              </p>
                            </div>

                            <div className="space-y-2">
                              {fields.map((item, index) => (
                                <div key={item.id} className="flex items-center gap-2">
                                  <FormField
                                    control={form.control}
                                    name={`additional_image_urls.${index}.url`} // Corrected syntax
                                    render={({ field }) => (
                                      <FormItem className="flex-grow">
                                        <FormControl>
                                          <Input
                                            type="url"
                                            placeholder={`URL da imagem ${index + 1}`} // Corrected syntax
                                            {...field}
                                            value={field.value || ''}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    size="icon"
                                    onClick={() => remove(index)}
                                    aria-label={`Remover imagem ${index + 1}`} // Corrected syntax
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>

                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => append({ url: '' })}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Adicionar URL
                            </Button>
                          </div>

                          {/* Preview grid for additional images */}
                          {(fields.length > 0 || additionalImageFiles.length > 0) && ( // Show preview if files are staged or already in form
                              <div className="mt-6">
                                  <h3 className="text-sm font-medium mb-2">Prévia das Imagens Adicionais</h3>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                      {/* Display existing/added URL fields */}
                                      {fields.map((field, index) => {
                                          const url = form.watch(`additional_image_urls.${index}.url`); // Corrected syntax
                                          // Render if URL is valid and not marked for deletion
                                          if (url && !imagesToDelete.includes(field.id || '')) {
                                              return (
                                                  <div key={field.id || `new-${index}`} className="border rounded-md overflow-hidden aspect-square relative group"> // Corrected syntax
                                                      <ImageHandler
                                                          src={url}
                                                          alt={`Imagem adicional ${index + 1}`} // Corrected syntax
                                                          className="w-full h-full object-cover"
                                                      />
                                                       {/* Only show delete for existing images with IDs */}
                                                       {field.id && (
                                                        <Button
                                                          type="button"
                                                          variant="destructive"
                                                          size="icon"
                                                          className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                                          onClick={() => {
                                                              handleRemoveExistingImage(field.id); // Mark for deletion
                                                              remove(index); // Remove from form array visually
                                                          }}
                                                        >
                                                          <Trash2 className="h-3 w-3" />
                                                        </Button>
                                                      )}
                                                  </div>
                                              );
                                          }
                                          return null;
                                      })}
                                  </div>
                              </div>
                          )}


                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>

                  <TabsContent value="details">
                    {/* Full Description */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Descrição Detalhada</CardTitle>
                        <CardDescription>
                          Forneça detalhes completos do produto (opcional).
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <FormField
                          control={form.control}
                          name="full_description"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Textarea
                                  placeholder="Descrição detalhada do produto..."
                                  className="min-h-[200px]"
                                  {...field}
                                  value={field.value || ''}
                                />
                              </FormControl>
                              <FormDescription>
                                Esta descrição será exibida na página de detalhes do produto.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Submit Button */}
                  <div className="pt-4 flex justify-end">
                    <Button
                      type="submit"
                      disabled={isUploading || form.formState.isSubmitting || createMutation.isPending || updateMutation.isPending}
                      size="lg"
                    >
                      {(isUploading || form.formState.isSubmitting || createMutation.isPending || updateMutation.isPending) ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {isUploading ? 'Enviando imagens...' : 'Salvando...'}
                        </>
                      ) : (
                        <>
                          <Save className="mr-2 h-4 w-4" />
                          {isEditing ? 'Atualizar Produto' : 'Criar Produto'}
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductForm;
